// @ts-nocheck

/* eslint-disable no-restricted-globals */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */

import type MacheteTypes from '@hover/machete';

import { FeatureCategories, Menu } from 'src/modules/designStudio/constants';
import { MacheteData, ProductEntry } from 'src/modules/designStudio/types';

// MacheteViewer types incomplete, using my own type for
// machine for now
type ExtendedMacheteViewer = {
  resize: (transitionTime?: number, delay?: number) => void;
};

interface MacheteType {
  building: object;
  viewer: object;
  originalFeaturesStyles: object;
}

interface FocusOptionsType {
  useOrbit: boolean;
  duration: number;
  offset?: number;
  moneyshotConfig?: {
    offset: number;
  };
}

const DEFAULT_FOCUS_OPTS = {
  moneyshotConfig: {
    offset: 1.2,
  },
};

const createSegmentationTree = (
  machete: Machete,
  building: MacheteTypes.Building,
) => {
  machete.Machete.Tools.WebSegmentation.configure({});
  machete.Machete.Tools.WebSegmentation.prepareModel(building);

  const tree = building.segmentationTree;

  tree.autoUpdate = true;
  tree.configure({ defaultDecorators: [] });

  const { createNodesFromModel } = machete.Machete.SegmentationUtils;

  tree.addNodes(createNodesFromModel(building));

  tree.start();
  tree.update();

  return building;
};

const initFeatures = (
  machete: Machete,
  building: MacheteTypes.Building,
  labels?: any,
) => {
  building.initFeatureAPI(building.segmentationTree, labels);
};

const setTextures = (machete: Machete, textures: ProductEntry[]) => {
  machete.TextureLibraryV2.setTextures(textures);
};

const load = async (machete: Machete, { geometry, metadata }: MacheteData) => {
  const building = new machete.Building();

  return new Promise<MacheteTypes.Building>(resolve => {
    building.load({
      geometry,
      metadata,
      dimensionality: 3,
      onComplete: () => resolve(building),
      useSyntheticGlassAnnotations: true,
    });
  });
};

const initViewer = (machete: Machete, element: HTMLElement) => {
  /**
   * To see all the options Machete allows to configure, check the interface definition at:
   * https://github.com/hoverinc/machete/blob/master/src/entities/Environment.ts#L8
   */
  const ultraHighQuality = new URLSearchParams(location.search).get(
    'ultraHighQuality',
  );

  const viewer = new machete.Machete.Viewer(element, {
    quality: ultraHighQuality === 'true' ? 4 : 1,
    webgl: {
      context: {
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
        premultipliedAlpha: true,
        autoClear: false,
      },
    },
    useOrbit: true,
    useGradientEnvironment: true,
    environmentMapTexture: 'assets/img/window-reflection.png',
    clearColorAlpha: 0.0,
    shadowOnlyBG: true,
  });

  viewer.renderer.setClearColor(0x000000, 0);
  viewer.controls.maxPolarAngle = Math.PI * 0.55;
  viewer.controls.noPan = true;
  viewer.controls.maxDistance = 30;

  return viewer;
};

const setBuilding = (
  viewer: MacheteTypes.Machete.Viewer,
  building: MacheteTypes.Building,
) => {
  viewer.modelContainer.addModel(building);
  viewer.focus(building, DEFAULT_FOCUS_OPTS);
};

const listenForEventFactory = (viewer: MacheteTypes.Machete.Viewer) => (
  event: string,
  callback: () => void,
) => {
  viewer.controls.addEventListener(event, callback);

  // unsubscriber
  return () => {
    viewer.controls.removeEventListener(event, callback);
  };
};

const getLabelsForCategory = (category: FeatureCategories) => {
  const {
    WALL_LABELS,
    ROOF_LABEL_ID,
    OPENINGS_TRIM_LABEL_ID,
    TRIM_LABEL_ID,
    WINDOW_LABEL_ID,
  } = Hover.Constants;

  let labels: number[];

  switch (category) {
    case FeatureCategories.WALL:
      labels = WALL_LABELS;
      break;

    case FeatureCategories.ROOF:
      labels = [ROOF_LABEL_ID];
      break;

    case FeatureCategories.TRIM:
      labels = [OPENINGS_TRIM_LABEL_ID, TRIM_LABEL_ID];
      break;

    case FeatureCategories.WINDOW:
      labels = [WINDOW_LABEL_ID];
      break;

    default:
      break;
  }

  return labels;
};

const getCategoryForFeatureLabel = featureLabel => {
  const labelsWall = getLabelsForCategory(FeatureCategories.WALL);
  const labelsRoof = getLabelsForCategory(FeatureCategories.ROOF);
  const labelsTrim = getLabelsForCategory(FeatureCategories.TRIM);
  const labelsWindow = getLabelsForCategory(FeatureCategories.WINDOW);

  if (labelsRoof.includes(featureLabel)) {
    return FeatureCategories.ROOF;
  }
  if (labelsTrim.includes(featureLabel)) {
    return FeatureCategories.TRIM;
  }
  if (labelsWall.includes(featureLabel)) {
    return FeatureCategories.WALL;
  }
  if (labelsWindow.includes(featureLabel)) {
    return FeatureCategories.WINDOW;
  }
  return null;
};

const getOriginalFeatureStyles = building => {
  const allFeatures = building.getFeatures();

  // Save original Data for walls, roof, trim for reset
  const originalFeaturesStyles = allFeatures.reduce(
    (acc, feature) => {
      const category = getCategoryForFeatureLabel(feature.primary_label);

      if (category === FeatureCategories.WINDOW) {
        acc[category].push({
          uuid: feature.uuid,
          color:
            feature.appearance_preset.parametricOptions.attributes.windowColor,
          frameType:
            feature.appearance_preset.parametricOptions.attributes
              .windowProduct,
          gridType:
            feature.appearance_preset.parametricOptions.attributes.windowGrille,
        });
      } else if (category) {
        acc[category].push({
          uuid: feature.uuid,
          color: feature.color,
          textureId: feature.appearance_preset.textureID,
        });
      }
      return acc;
    },
    {
      [FeatureCategories.WALL]: [],
      [FeatureCategories.TRIM]: [],
      [FeatureCategories.ROOF]: [],
      [FeatureCategories.WINDOW]: [],
    },
  );

  return originalFeaturesStyles;
};

const getFocusOptionsForMenu = async (building, viewer, menu) => {
  const focusConfigs = {
    [Menu.ROOF]: {
      offset: 1.2,
      moneyshotConfig: {
        offset: 2,
      },
    },
    [Menu.WALL]: {
      side: Hover.FrontMoneyshot,
      offset: 0.65,
    },
    [Menu.WINDOW]: {
      moneyshotConfig: {
        offset: 1.8,
      },
    },
  };
  const focusConfig = menu === null ? DEFAULT_FOCUS_OPTS : focusConfigs[menu];
  const focusOptions: FocusOptionsType = {
    useOrbit: true,
    duration: 750,
    ...focusConfig,
  };

  let focusElement: object = building;

  if (menu === Menu.ROOF) {
    const roofFeatures = await building.findFeaturesByAttributes({
      primary_label: Hover.Constants.ROOF_LABEL_ID,
    });

    [focusElement] = roofFeatures;
  } else if (menu === Menu.WINDOW) {
    const windowFeatures = await building.findFeaturesByAttributes({
      primary_label: Hover.Constants.WINDOW_LABEL_ID,
    });

    [focusElement] = windowFeatures;
  }

  return { focusElement, focusOptions };
};

const timeout = ms => new Promise(resolve => setTimeout(resolve, ms));

const changeCameraFocus = async (viewer, focusElement, focusOptions, delay) => {
  // allow menu open/close transition to complete
  await timeout(delay);

  viewer.focus(focusElement, focusOptions);
  viewer.controls.useOrbitMode = false;
  viewer.controls.update();

  await timeout(focusOptions.duration);

  viewer.controls.useOrbitMode = true;
  viewer.controls.update();
};

const renderMachete = async ({
  canvasRef,
  textures,
  geometry,
  metadata,
}): Promise<MacheteType> => {
  const machete = (
    await import(
      /* webpackChunkName: "machete" */
      '@hover/machete'
    )
  ).default;

  setTextures(machete, textures);

  const viewer = initViewer(machete, canvasRef.current);
  const building = await load(machete, { geometry, metadata });

  setBuilding(viewer, building);

  // The `createSegmentationTree` method ultimately applies textures to the
  // model which is performance intensive so we may want to optimize this later
  createSegmentationTree(machete, building);
  initFeatures(machete, building);

  // Save original Data for walls, roof, trim for reset
  const originalFeaturesStyles = getOriginalFeatureStyles(building);

  await building.isReady();

  return { building, viewer, originalFeaturesStyles };
};

const generateAppearancePreset = (building, style) => {
  let newAppearance;
  if (style.textureId) {
    newAppearance = building.makeAppearancePresetFromTextureID(style.textureId);
  } else {
    newAppearance = building.makeAppearancePresetFromParametricAttributes({
      gridType: style.gridType,
      frameType: style.frameType,
    });
  }

  newAppearance.color = style.color;
  return newAppearance;
};

const resetStylesToOriginal = ({ building, ogStyles }) => {
  ogStyles.forEach(feature => {
    const featureWithUUID = building.getFeatureByUUID(feature.uuid);
    featureWithUUID.appearance_preset = generateAppearancePreset(
      building,
      feature,
    );
  });
};

const isStyleEmpty = ({ category, update }) => {
  if (category === FeatureCategories.WINDOW) {
    return (
      update.color === null &&
      update.gridType === null &&
      update.frameType === null
    );
  }
  return update.color === null && update.textureId === null;
};

const updateStyles = ({
  building,
  category,
  update,
  originalFeaturesStyles,
}) => {
  if (isStyleEmpty({ category, update })) {
    const ogStyles = originalFeaturesStyles[category];
    resetStylesToOriginal({ building, ogStyles });
    return;
  }
  const allFeatures = building.getFeatures();
  const labels = getLabelsForCategory(category);

  const features = allFeatures.filter(feature =>
    labels.includes(feature.primary_label),
  );

  const newAppearance = generateAppearancePreset(building, update);

  features.forEach(feature => {
    feature.appearance_preset = newAppearance;
  });
};

const takeScreenshot = async (viewer): Promise<Blob> => {
  const maintainAspectRatio = true;
  const screenshot = await viewer.takeScreenshot({
    maintainAspectRatio,
    width: 500,
  });

  return screenshot;
};

export {
  changeCameraFocus,
  ExtendedMacheteViewer,
  getCategoryForFeatureLabel,
  getFocusOptionsForMenu,
  renderMachete,
  updateStyles,
  takeScreenshot,
  listenForEventFactory,
};
